import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { usePortalApi } from "./portal-api";
import { portalServer } from "../config";
import {
  useSyncData,
  useSyncModuleRegistration,
  type SyncModule,
} from "../sync";
import type { Information } from "../types";
import { limiter } from "./demo";
import { daily } from "./provider";

const syncModuleId = "informationen";

function useInformationenSyncModule(): SyncModule<ReadonlyArray<Information>> {
  const { portalKey, demo } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `tasks:${syncModuleId}`,
      defaultOptions: {
        schedule: { type: "periodic", interval: daily },
      },
      fetchData: useNewApi && baseUrl
        ? () => fetchInformationenNew(portalKey, baseUrl)
        : async () => {
            const response = await fetch(
              `${portalServer}/get-informations.php?ORG_ACCESSKEY=${encodeURIComponent(
                portalKey,
              )}`,
            );

            if (!response.ok) {
              throw new Error(
                `Error fetching data: ${response.status} ${response.statusText}`,
              );
            }

            const data = await response.json();
            const limited = limiter<Information>(demo, 3);
            return limited(data.INFORMATION_LIST ?? []).map(parseInformationLegacy);
          },
    }),
    [demo, portalKey, useNewApi, baseUrl],
  );
}

async function fetchInformationenNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<Information>> {
  const response = await fetch(
    `${baseUrl}/api/device/sync/informations?includeArchived=false`,
    {
      headers: { Authorization: `Bearer ${portalKey}` },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.information ?? []).map(parseInformationNew);
}

function parseInformationLegacy(i: any): Information {
  return {
    id: i.INFORMATION_ID,
    text: i.INFORMATION_TEXT,
    von: i.INFORMATION_VALID_FROM,
    bis: i.INFORMATION_VALID_TO,
  };
}

function parseInformationNew(i: any): Information {
  return {
    id: i.id,
    text: i.description,
    von: i.dateFrom ?? "",
    bis: i.dateTo ?? "",
  };
}

export function useInformationenSync() {
  const module = useInformationenSyncModule();
  useSyncModuleRegistration(module);
}

export function useInformationen(): ReadonlyArray<Information> {
  return useSyncData(syncModuleId) ?? [];
}
